import { Settings } from 'luxon';
import { BindFancybox } from 'smg/fancybox';
import { SetBootstrapPrefix } from 'smg/bootstrap';
import { Browser } from 'smg/browser';
import { GetCartID } from 'smg/customer-api/v2/carts';
import { GetPageRequestID } from 'smg/customer-api/pagerequests';
import * as session from 'smg/customer-api/session';
import * as errors from 'smg/errors';
import { InitGoogleCredentialHandler } from 'smg/googlesignin';
import { SetIconLibrary } from 'smg/icons';
import Modal, { SetDefaults } from 'smg/modal';
import * as phones from 'smg/phone';
import QueryString from 'smg/querystring';
import { SetDefaultToastClasses, SetDefaultToastCloseBtnClasses } from 'smg/toast';
import { Relref } from './hugo';

import './pricing';
import './time';

import * as meta from 'smg/meta';
import * as pinterest from 'smg/pinterest';

import * as carts from './carts';
import * as login from './login';

import './components/buttons';
import './components/components';
import './components/dropdowns';
import './components/filters';
import './components/footer';
import './components/forms/form-checks';
import './components/forms/form-control';
import './components/gallery';
import './components/header';
import './components/jump-to-top';
import './components/toasts';

import * as html from 'smg/html';

// Luxon formats with the browser's locale unless told otherwise; the page's
// language is the one the copy around the date is in.
Settings.defaultLocale = document.documentElement.lang;

declare interface myWindow extends Window {
    getPageRequestID: typeof GetPageRequestID;
    redditPixel?: string;
    pinterestPixel?: string;
    metaPixel?: string;
}
declare const window: myWindow;

window.getPageRequestID = GetPageRequestID;

errors.InitClientSideErrorLogging();

SetDefaults({
    cancelButtonClass: 'btn btn-outline-primary',
    init: (modal: Modal<unknown>) => {
        const modalError = modal.$element.querySelector('.modal-error') as HTMLElement;
        const modalBody = modal.$element.querySelector('.modal-body') as HTMLElement;
        const modalFooter = modal.$element.querySelector('.modal-footer') as HTMLElement;
        modalFooter.classList.add('d-flex', 'justify-content-center', 'gap-4');
        modalError.className = 'd-none';
        modalError.classList.add('smg-modal-error', 'alert', 'alert-danger', 'mb-8', 'pb-5');
        modalBody?.insertBefore(modalError, modalBody.firstElementChild);
    },
});

errors.Defaults.showErrorFunc = msg => new Modal({
    size: 'modal-sm',
    body: html.FromString(`<div class="d-flex flex-column align-items-center text-center">
            <div class="icon-mark icon-mark-lg mb-6">
                <span class="material-symbols-outlined">error</span>
            </div>
            <h2 class="fw-900 mb-2">An error occurred</h2>
            <p class="mb-2">We encountered an unexpected issue. If this message continues to appear,
            please take a screenshot and contact our support team so we can help resolve it.</p>
            <code class="w-100 p-1 bg-light text-center">${html.Escape(msg)}</code>
        </div>`),
    cancelText: 'Ok',
    cancelButtonClass: 'btn btn-primary mx-auto',
    appendClassBySelector: {
        '.modal-body': 'px-6 pt-8 pb-2',
        '.modal-footer': 'p-6 pt-sm-10 pb-sm-14 justify-content-center',
    },
});

BindFancybox();

SetIconLibrary('materialSymbolsRounded');

SetDefaultToastClasses(['align-items-center']);
SetDefaultToastCloseBtnClasses(['me-2', 'm-auto']);

SetBootstrapPrefix('smg-');

phones.SetDefaults({
    customPlaceholder: str => str,
});

// Live unit pricing hydrates rows marked up with data-line-item/-product-type/-size.
// That markup appears across pricing, product-detail, all-pricing and usage pages —
// not a single page — but it pulls in the lineitems/Big deps, so lazy-load the
// implementation only when such a row is present rather than on every page.
if (document.querySelector('[data-line-item][data-product-type][data-size]')) {
    void import('./components/live-pricing').catch(err => console.error(`failed to load live-pricing: ${String(err)}`));
}

// The product-type quiz pulls showdown/luxon/modal but only runs when a quiz
// link is clicked. Keep a lightweight delegated listener here and lazy-load the
// implementation on first click, so pages that merely render a quiz link (home,
// landings) never download those deps.
document.addEventListener('click', e => {
    const $trigger = (e.target as HTMLElement).closest<HTMLElement>('[data-product-type-quiz-for]');
    if (!$trigger) {
        return;
    }
    e.preventDefault();
    // preventDefault already fired, so a failed chunk load would be a silent
    // dead click — surface it instead of swallowing the rejection.
    void import('./components/product-type-quiz').
        then(quiz => quiz.OpenProductTypeQuiz($trigger)).
        catch(err => errors.ShowError(err));
});

// role="button" collapse togglers (quote steps, FAQ questions) are <div>s, so the
// browser never synthesizes a click on Enter/Space the way it does for native
// buttons. Bridge keyboard activation to the click Bootstrap's collapse data-api
// listens for. Native <a>/<button> togglers already handle their own keys — exclude
// them so they don't fire twice.
document.addEventListener('keydown', e => {
    if (e.key !== 'Enter' && e.key !== ' ') {
        return;
    }
    const $toggler = document.activeElement?.closest<HTMLElement>('[data-bs-toggle="collapse"][role="button"]:not(a):not(button)');
    if (!$toggler) {
        return;
    }
    e.preventDefault();
    $toggler.click();
});

async function initCart() {
    if (QueryString().get('deleteCart')) {
        await carts.DeleteCart().catch(() => undefined);
        QueryString().delete('deleteCart');
    }

    if (!GetCartID()) {
        return;
    }

    carts.
        GetCartCount().
        then(count => {
            if (!count) {
                return;
            }

            carts.IncrementCartCount(count);
        }).
        catch(err => console.error(`failed to get cart count: ${String(err)}`));
}

const sess = session.Session().catch(() => undefined);

// On checkout, every way of signing in comes back to the checkout, as its invitation does.
const onCheckout = document.querySelector('#checkout-sign-in') !== null;
InitGoogleCredentialHandler(onCheckout ? `${window.location.pathname}?signedIn=1` : undefined);

const $accountButton = document.querySelectorAll<HTMLAnchorElement>('.account-button');
for (const $btn of Array.from($accountButton)) {
    $btn.addEventListener('click', async e => {
        e.preventDefault();

        if (await sess === undefined) {
            const $mobileMenu = document.querySelector<HTMLElement>('#mobile-nav-offcanvas');
            const $closeBtn = $mobileMenu?.querySelector<HTMLButtonElement>('.close-btn');
            $closeBtn?.click();
            login.PhoneLoginModal(undefined, onCheckout ? login.CheckoutLoginFlow : login.DefaultLoginFlow);
        } else {
            window.location.href = Relref('/account');
        }
    });
}

const $logoutButton = document.querySelectorAll<HTMLAnchorElement>('.logout-button');
$logoutButton.forEach($ => {
    $.addEventListener('click', e => {
        e.preventDefault();

        session.DeleteSession();
        window.location.href = Relref('/');
    });
});

async function main() {
    // Initialize page tracking
    void GetPageRequestID();

    for (const $ of Array.from(document.querySelectorAll('.hidden-with-signin'))) {
        if (await sess === undefined) {
            $.classList.remove('d-none');
        } else {
            $.classList.add('d-none');
        }
    }

    for (const $ of Array.from(document.querySelectorAll('.visible-with-signin'))) {
        if (await sess === undefined) {
            $.classList.add('d-none');
        } else {
            $.classList.remove('d-none');
        }
    }

    await session.ConsumeLoginTokenFromURL();

    const returnLoc = QueryString().get('return');
    if (returnLoc) {
        login.PhoneLoginModal();
    }

    document.body.classList.add(`browser-${Browser()}`);

    void initCart();

    // tiny-slider drives the homepage product carousel only; lazy-load it (and
    // its bundle) so every other page skips the download.
    const $sliderContainer = document.querySelector<HTMLElement>('#slider-container');
    const $prevButton = document.querySelector<HTMLElement>('#prev-btn');
    const $nextButton = document.querySelector<HTMLElement>('#next-btn');
    if ($sliderContainer && $prevButton && $nextButton) {
        void import('./components/home-slider').
            then(m => m.InitHomeSlider($sliderContainer, $prevButton, $nextButton)).
            catch(err => console.error(`failed to load home slider: ${String(err)}`));
    }

    if (window.rdt) {
        const rdt = window.rdt;
        void sess.then(sess => {
            if (sess) {
                rdt('init', window.redditPixel, {
                    'email': sess.user.emailAddress.email,
                    'phoneNumber': sess.user.phoneNumber.phone,
                    'externalId': sess.user.id,
                });
            } else {
                rdt('init', window.redditPixel);
            }
        }).finally(() => {
            rdt('track', 'PageVisit');
        });
    }

    // Initialize Pinterest pixel tracking
    if (window.pinterestPixel) {
        const pixelId = window.pinterestPixel;
        void sess.then(sess => {
            pinterest.InitializePinterest(pixelId, sess?.user.emailAddress.email);
        }).catch(() => {
            pinterest.InitializePinterest(pixelId);
        }).finally(() => {
            pinterest.TrackPageVisit();
        });
    }

    // Initialize Meta (Facebook) pixel tracking
    if (window.metaPixel) {
        const pixelId = window.metaPixel;
        void sess.then(sess => {
            meta.InitializeMeta(pixelId, sess ? {
                email: sess.user.emailAddress.email,
                phone: sess.user.phoneNumber.phone,
                userId: sess.user.id,
            } : undefined);
        }).catch(() => {
            meta.InitializeMeta(pixelId);
        }).finally(() => {
            meta.TrackPageView();
        });
    }
}

void main();
